import { useRef, useMemo, useEffect, useState } from 'react';
import * as THREE from 'three';
import { useGameStore } from '../store/gameStore';
import type { Track } from '../types/city';

interface StageProps {
  track: Track;
  position: [number, number, number];
  districtColor: string;
  onPositionRegistered?: (trackId: string, position: [number, number, number], size: [number, number, number]) => void;
}

/**
 * Festival stage replacing the old Building component.
 * Features: raised platform, truss backdrop with album art,
 * speaker stacks, DJ table with decks, and a low-poly DJ figure.
 */
export default function Stage({ track, position, districtColor, onPositionRegistered }: StageProps) {
  const meshRef = useRef<THREE.Group>(null);
  const registered = useRef(false);
  const [artworkTexture, setArtworkTexture] = useState<THREE.Texture | null>(null);
  const lockedTrackId = useGameStore((s) => s.lockedTrackId);
  const isLocked = lockedTrackId === track.id;

  // Fixed stage dimensions — festival-scale
  const SW = 7;   // stage width
  const SD = 5;   // stage depth
  const SH = 0.6; // stage height
  const BW = 8;   // backdrop width
  const BH = 5;   // backdrop height

  // Stage edge Z (front of stage)
  const stageFrontZ = SD / 2;
  // Backdrop Z (behind stage)
  const backdropZ = -SD / 2 - 0.5;
  // DJ table position relative to stage center
  const tableZ = SD / 2 - 1.5;
  const tableY = SH + 0.5;

  // Load artwork texture — robust pattern handling cached + fresh loads
  useEffect(() => {
    if (!track.albumArt) return;
    let cancelled = false;
    const img = new Image();

    const handleLoad = () => {
      if (cancelled) return;
      const tex = new THREE.Texture(img);
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.needsUpdate = true;
      setArtworkTexture(tex);
    };

    const handleError = () => {
      if (cancelled) return;
      console.warn(`Failed to load artwork: ${track.albumArt}`);
      // Retry once after a short delay (browser cache may have been cold)
      if (!img.dataset.retried) {
        img.dataset.retried = '1';
        setTimeout(() => { if (!cancelled) img.src = track.albumArt; }, 500);
      }
    };

    img.onload = handleLoad;
    img.onerror = handleError;
    // Set src AFTER attaching handlers — prevents missed onload for cached images
    // Also check img.complete in case onload fired synchronously (some browsers)
    img.src = track.albumArt;
    if (img.complete && !cancelled) {
      handleLoad();
    }

    return () => { cancelled = true; };
  }, [track.albumArt]);

  const handleClick = () => {
    useGameStore.getState().openModal(track.id);
  };

  // Deterministic accent color from track id
  const accentColor = useMemo(() => {
    let hash = 0;
    for (let i = 0; i < track.id.length; i++) {
      hash = ((hash << 5) - hash) + track.id.charCodeAt(i);
      hash |= 0;
    }
    return ((hash * 16807) % 2147483647) / 2147483646 > 0.5 ? '#ff6b6b' : '#ffd93d';
  }, [track.id]);

  // Register stage position for proximity engine + collision
  if (!registered.current) {
    registered.current = true;
    setTimeout(() => onPositionRegistered?.(track.id, position, [SW, SH, SD]), 0);
  }

  return (
    <group ref={meshRef} position={position} onClick={handleClick}>
      {/* Lock indicator — golden ring */}
      {isLocked && (
        <mesh position={[0, SH + 0.3, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[4.5, 0.15, 16, 4]} />
          <meshBasicMaterial color="#FFD700" />
        </mesh>
      )}

      {/* ===== STAGE PLATFORM ===== */}
      <mesh position={[0, SH / 2, 0]} castShadow receiveShadow>
        <boxGeometry args={[SW, SH, SD]} />
        <meshStandardMaterial color="#2a2a2a" roughness={0.4} metalness={0.3} />
      </mesh>

      {/* Stage edge trim — colored accent strip */}
      <mesh position={[0, SH, stageFrontZ + 0.05]} receiveShadow>
        <boxGeometry args={[SW + 0.1, 0.08, 0.15]} />
        <meshStandardMaterial
          color={accentColor}
          roughness={0.3}
          emissive={accentColor}
          emissiveIntensity={0.3}
        />
      </mesh>

      {/* Stage surface — slightly lighter top */}
      <mesh position={[0, SH + 0.01, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[SW, SD]} />
        <meshBasicMaterial color="#333333" />
      </mesh>

      {/* ===== BACKDROP TRUSS ===== */}
      {/* Left pole */}
      <mesh position={[-BW / 2, SH + BH / 2, backdropZ]} castShadow>
        <cylinderGeometry args={[0.12, 0.12, BH, 8]} />
        <meshStandardMaterial color="#444444" roughness={0.5} metalness={0.6} />
      </mesh>
      {/* Right pole */}
      <mesh position={[BW / 2, SH + BH / 2, backdropZ]} castShadow>
        <cylinderGeometry args={[0.12, 0.12, BH, 8]} />
        <meshStandardMaterial color="#444444" roughness={0.5} metalness={0.6} />
      </mesh>
      {/* Top bar */}
      <mesh position={[0, SH + BH, backdropZ]} castShadow>
        <boxGeometry args={[BW, 0.12, 0.12]} />
        <meshStandardMaterial color="#444444" roughness={0.5} metalness={0.6} />
      </mesh>
      {/* Bottom bar */}
      <mesh position={[0, SH + 0.1, backdropZ]}>
        <boxGeometry args={[BW, 0.1, 0.1]} />
        <meshStandardMaterial color="#444444" roughness={0.5} metalness={0.6} />
      </mesh>

      {/* ===== BACKDROP SCREEN ===== */}
      {/* Dark panel — DoubleSide so it's solid from any angle */}
      <mesh position={[0, SH + BH / 2, backdropZ + 0.2]}>
        <planeGeometry args={[BW - 0.5, BH - 0.3]} />
        <meshBasicMaterial color="#0a0a1e" side={THREE.DoubleSide} />
      </mesh>

      {/* Album art on backdrop — normal depth testing so it doesn't clip through the DJ */}
      <mesh position={[0, SH + BH / 2, backdropZ + 0.21]}>
        <planeGeometry args={[BW - 1.5, BH - 1.5]} />
        <meshBasicMaterial
          color={artworkTexture ? '#ffffff' : '#151530'}
          map={artworkTexture || undefined}
        />
      </mesh>

      {/* ===== SPEAKER STACKS ===== */}
      {/* Left stack */}
      <group position={[-SW / 2 - 0.5, SH, stageFrontZ - 0.5]}>
        <mesh position={[0, 0.5, 0]} castShadow>
          <boxGeometry args={[0.6, 1.0, 0.7]} />
          <meshStandardMaterial color="#1a1a1a" roughness={0.7} />
        </mesh>
        <mesh position={[0, 0.98, 0]}>
          <circleGeometry args={[0.18, 16]} />
          <meshBasicMaterial color="#111" />
        </mesh>
      </group>
      {/* Right stack */}
      <group position={[SW / 2 + 0.5, SH, stageFrontZ - 0.5]}>
        <mesh position={[0, 0.5, 0]} castShadow>
          <boxGeometry args={[0.6, 1.0, 0.7]} />
          <meshStandardMaterial color="#1a1a1a" roughness={0.7} />
        </mesh>
        <mesh position={[0, 0.98, 0]}>
          <circleGeometry args={[0.18, 16]} />
          <meshBasicMaterial color="#111" />
        </mesh>
      </group>

      {/* ===== DJ TABLE ===== */}
      <mesh position={[0, tableY + 0.45, tableZ]} castShadow>
        <boxGeometry args={[2.2, 0.9, 0.9]} />
        <meshStandardMaterial color="#1a1a1a" roughness={0.5} metalness={0.3} />
      </mesh>
      {/* Table top */}
      <mesh position={[0, tableY + 0.93, tableZ]}>
        <boxGeometry args={[2.3, 0.06, 1.0]} />
        <meshStandardMaterial color="#2a2a2a" roughness={0.3} metalness={0.5} />
      </mesh>
      {/* Left deck (turntable) */}
      <mesh
        position={[-0.5, tableY + 0.97, tableZ]}
        rotation={[-Math.PI / 2, 0, 0]}
      >
        <cylinderGeometry args={[0.25, 0.25, 0.03, 24]} />
        <meshStandardMaterial color="#3a3a3a" roughness={0.2} metalness={0.6} />
      </mesh>
      {/* Right deck */}
      <mesh
        position={[0.5, tableY + 0.97, tableZ]}
        rotation={[-Math.PI / 2, 0, 0]}
      >
        <cylinderGeometry args={[0.25, 0.25, 0.03, 24]} />
        <meshStandardMaterial color="#3a3a3a" roughness={0.2} metalness={0.6} />
      </mesh>

      {/* ===== DJ FIGURE (behind the table, facing audience) ===== */}
      <group position={[0, SH + 0.12, tableZ - 0.7]}>
        {/* Left leg */}
        <mesh position={[-0.18, 0.5, 0]}>
          <cylinderGeometry args={[0.1, 0.12, 0.65, 8]} />
          <meshStandardMaterial color="#1a1a2e" roughness={0.8} />
        </mesh>
        {/* Right leg */}
        <mesh position={[0.18, 0.5, 0]}>
          <cylinderGeometry args={[0.1, 0.12, 0.65, 8]} />
          <meshStandardMaterial color="#1a1a2e" roughness={0.8} />
        </mesh>
        {/* Torso */}
        <mesh position={[0, 1.2, 0]} castShadow>
          <boxGeometry args={[0.5, 0.5, 0.28]} />
          <meshStandardMaterial color={districtColor} roughness={0.5} />
        </mesh>
        {/* Head — tall enough to be visible above the table */}
        <mesh position={[0, 1.65, 0]} castShadow>
          <sphereGeometry args={[0.17, 12, 8]} />
          <meshStandardMaterial color="#f5c6a0" roughness={0.7} />
        </mesh>
        {/* Headphones band */}
        <mesh position={[0, 1.65, 0]}>
          <torusGeometry args={[0.17, 0.025, 8, 16, Math.PI]} />
          <meshStandardMaterial color="#333" roughness={0.3} />
        </mesh>
        {/* Headphones cups */}
        <mesh position={[-0.17, 1.65, 0]}>
          <boxGeometry args={[0.04, 0.08, 0.06]} />
          <meshStandardMaterial color="#333" roughness={0.3} />
        </mesh>
        <mesh position={[0.17, 1.65, 0]}>
          <boxGeometry args={[0.04, 0.08, 0.06]} />
          <meshStandardMaterial color="#333" roughness={0.3} />
        </mesh>
        {/* Left arm reaching forward to deck */}
        <mesh
          position={[-0.3, 1.35, 0.15]}
          rotation={[1.4, 0, 0.1]}
        >
          <cylinderGeometry args={[0.05, 0.06, 0.55, 8]} />
          <meshStandardMaterial color="#f5c6a0" roughness={0.7} />
        </mesh>
        {/* Left hand — at arm endpoint */}
        <mesh position={[-0.3, 1.4, 0.42]}>
          <sphereGeometry args={[0.06, 8, 6]} />
          <meshStandardMaterial color="#f5c6a0" roughness={0.7} />
        </mesh>
        {/* Right arm reaching forward to deck */}
        <mesh
          position={[0.3, 1.35, 0.15]}
          rotation={[1.4, 0, -0.1]}
        >
          <cylinderGeometry args={[0.05, 0.06, 0.55, 8]} />
          <meshStandardMaterial color="#f5c6a0" roughness={0.7} />
        </mesh>
        {/* Right hand — at arm endpoint */}
        <mesh position={[0.3, 1.4, 0.42]}>
          <sphereGeometry args={[0.06, 8, 6]} />
          <meshStandardMaterial color="#f5c6a0" roughness={0.7} />
        </mesh>
      </group>

      {/* ===== SONGS SIGN (above backdrop) ===== */}
      <mesh position={[0, SH + BH + 0.5, backdropZ + 0.2]}>
        <planeGeometry args={[BW - 0.5, 0.5]} />
        <meshBasicMaterial
          color={accentColor}
          transparent
          opacity={0.8}
        />
      </mesh>
    </group>
  );
}
